import { MobileNotification, ui } from "@workspace/mobile";

/**
 * Handle calls from the native device/bridge when a notification was tapped.
 * Data is decided by the application posting the notification.
 * Title and message are required. CustomData can be anything, such as plain text or a JSON object.
 * @param notification The notification data recieved by the device
 */
export const handleNotificationReceived = async (
  notification: MobileNotification
) => {
  // use additional customData if passed via your notifications
  const { title, message } = notification;

  // The notification might just be show an alert in the page
  // Or you may want to navigate them to a view or other popup.
  await ui.displayAlert({
    title,
    message,
    accept: "Okay"
  });
};

/**
 * NOTE the notification may have come from a DIFFERENT subscription to what we are running at present.
 * You will need to verify the subscription against your active organisation and present an option to the user.
 * For example, if it shows the notification and gives the option to view a record, you will want to switch the org.
 * If they cancel because they are mid workflow, then remain.
 * @param notification The notification data recieved by the device
 * @param currentSubscriptionId The current active subscription id
 * @param switchSubscription Callback to fire the switch of a subscription in the user context
 * @param promptUser In most cases, we will want to ask the user if they want to switch
 * @returns boolean indicating if the handling should proceed
 */
export const trySwitchSubscriptionFromNotification = async (
  notification: MobileNotification,
  currentSubscriptionId: string,
  switchSubscription: (subscriptionId: string) => void,
  promptUser = true
): Promise<boolean> => {
  if (
    notification.subscriptionId &&
    notification.subscriptionId !== currentSubscriptionId
  ) {
    if (!promptUser) {
      switchSubscription(notification.subscriptionId);
      return true;
    }

    const makeSwitch = await ui.displayConfirm({
      title: "Switch Subscription",
      message:
        "The link you clicked was for another subscription, would you like to swap to that now?",
      accept: "Okay",
      cancel: "Cancel"
    });

    if (makeSwitch) {
      try {
        switchSubscription(notification.subscriptionId);
        return true;
      } catch {
        // If the subscription could not be switched to..
        await ui.displayAlert({
          title: "Error",
          message: "An error occurred whilst switching organisation",
          accept: "Okay"
        });
      }
    }

    return false;
  }

  return true;
};
